import {
  Box,
  Flex,
  HStack,
  InputRightElement,
  keyframes,
} from "@chakra-ui/react";
import React, { ChangeEvent } from "react";
import { usePopper } from "react-popper";
import { useDebounce } from "use-debounce";
import { useBooleanState } from "../../../../hooks";
import { ChevronDownIcon, CloseIcon } from "../../../../icons";
import { Button } from "../../../common";
import { FormControlWrapper } from "../../FormControlWrapper";
import { DropDownOptionElement } from "./components";
import {
  VeriCustomSelectOption,
  VeriCustomSelectProps,
} from "./VeriCustomSelect.types";

const resultsDropDownOpenAnimationKeyFrames = keyframes`
  from {margin-top: 10px;opacity: 0;}
  to {margin-top: 0;opacity: 1;}
`;

const resultsDropDownCloseAnimationKeyFrames = keyframes`
  from {margin-top: 0;opacity: 1;}
  to {margin-top: 10px;opacity: 0;}
`;

const VeriCustomSelect = <T extends Record<string, any> = Record<string, any>>({
  onFilter,
  filterDelay,
  optionProps,
  renderOption,
  renderValueOption,
  wrapperOptions,
  placeholder,
  onChange,
  isDisabled,
  valueOption,
  updateCounter,
  isCancellable = true,
  ...rest
}: VeriCustomSelectProps<T>) => {
  const [rootElementRef, setRootElementRef] =
    React.useState<HTMLDivElement | null>(null);

  const [triggerElRef, setTriggerElRef] =
    React.useState<HTMLInputElement | null>(null);

  const [listElement, setListItem] = React.useState<HTMLDivElement | null>(
    null,
  );

  const {
    styles,
    attributes,
    update: updatePopper,
  } = usePopper(rootElementRef, listElement, {
    strategy: "fixed",
    modifiers: [
      {
        name: "offset",
        enabled: true,
        options: {
          offset: [0, 6],
        },
      },
    ],
  });

  const [selectedElement, setSelectedElement] = React.useState(-1);
  const [searchFilter, setSearchFilter] = React.useState("");
  const [debouncedSearchFilter] = useDebounce(searchFilter, filterDelay || 0);
  const [filterResults, setFilterResults] = React.useState<
    (VeriCustomSelectOption & T)[]
  >([]);
  const [selectedOption, setSelectedOption] = React.useState<
    (VeriCustomSelectOption & T) | null
  >(null);

  const isOpen = useBooleanState(false);
  const isOpenOnce = useBooleanState(false);

  if (selectedOption && selectedOption.value && !searchFilter) {
    const selectedResultIndex = filterResults.findIndex(
      (option) => option.value === selectedOption.value,
    );

    if (selectedResultIndex === -1) {
      if (searchFilter === "") {
        filterResults.unshift(selectedOption);
      } else {
        filterResults.push(selectedOption);
      }
    }
  }

  const onFilterInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchFilter(e.target.value || "");
  };

  const selectOptionByIndex = (index: number, scrollTo = false) => {
    setSelectedElement(index);

    if (scrollTo) {
      setTimeout(() => {
        if (index > -1 && listElement) {
          const visibleArea = listElement.scrollTop + listElement.offsetHeight;
          const selectedRef = listElement.querySelector<HTMLDivElement>(
            `[data-option-index="${index}"]`,
          );

          if (
            selectedRef &&
            selectedRef.offsetTop + selectedRef.offsetHeight > visibleArea
          ) {
            listElement.scrollTop =
              selectedRef.offsetTop +
              selectedRef.offsetHeight -
              listElement.offsetHeight;
          } else if (
            selectedRef &&
            selectedRef.offsetTop < listElement.scrollTop
          ) {
            listElement.scrollTop = selectedRef.offsetTop;
          }
        }
      }, 10);
    }
  };

  const _selectPrev = React.useCallback(() => {
    let newSelectedElement = 0;

    if (!filterResults.length) {
      newSelectedElement = 0;
    } else if (selectedElement - 1 < 0) {
      newSelectedElement = filterResults.length - 1;
    } else {
      newSelectedElement = selectedElement - 1;
    }

    if (
      filterResults[newSelectedElement] &&
      filterResults[newSelectedElement].disabled
    ) {
      let prevElement = null;
      for (let i = newSelectedElement; i >= 0; i--) {
        if (!filterResults[i].disabled) {
          prevElement = i;
          break;
        }
      }
      if (prevElement === null) {
        return -1;
      } else newSelectedElement = prevElement;
    }

    selectOptionByIndex(newSelectedElement, true);

    return newSelectedElement;
  }, [selectedElement, filterResults]);

  const _selectNext = React.useCallback(() => {
    let newSelectedElement = 0;

    if (
      !filterResults.length ||
      selectedElement + 1 > filterResults.length - 1
    ) {
      newSelectedElement = 0;
    } else {
      newSelectedElement = selectedElement + 1;
    }

    if (
      filterResults[newSelectedElement] &&
      filterResults[newSelectedElement].disabled
    ) {
      let nextElement = null;

      for (let i = newSelectedElement; i < filterResults.length; i++) {
        if (!filterResults[i].disabled) {
          nextElement = i;
          break;
        }
      }

      if (nextElement === null) return -1;
      else newSelectedElement = nextElement;
    }

    selectOptionByIndex(newSelectedElement, true);

    return newSelectedElement;
  }, [selectedElement, filterResults]);

  const handleUserKeyPress = React.useCallback(
    (event) => {
      const { keyCode } = event;

      isOpen.true();

      if (keyCode === 38) {
        _selectPrev();
        event.preventDefault();
      } else if (keyCode === 40) {
        _selectNext();
        event.preventDefault();
      } else if (keyCode === 13) {
        event.preventDefault();

        if (filterResults[selectedElement]) {
          onOptionSelect(filterResults[selectedElement]);
        }
      }
    },
    [_selectNext, _selectPrev],
  );

  const openDropDown = () => {
    if (isDisabled) return;
    isOpen.true();

    if (!isOpenOnce.state) {
      isOpenOnce.true();
    }

    setTimeout(() => {
      triggerElRef?.focus();

      if (selectedOption) {
        const selectedOptionIndex = filterResults.findIndex(
          (option) => option.value === selectedOption.value,
        );

        return selectOptionByIndex(
          selectedOptionIndex > -1 ? selectedOptionIndex : 0,
          true,
        );
      }

      return selectOptionByIndex(0, true);
    }, 10);
  };

  const closeDropDown = () => {
    isOpen.false();
    triggerElRef?.blur();
    setSearchFilter("");
  };

  const onRootElClick = () => {
    openDropDown();
  };

  const onOutsideClick = React.useCallback(
    (event) => {
      if (rootElementRef && !rootElementRef.contains(event.target)) {
        closeDropDown();
      }
    },
    [rootElementRef],
  );

  const onOptionSelect = (option: VeriCustomSelectOption & T) => {
    onChange?.(option.value, option);

    setSelectedOption(option);
    closeDropDown();
  };

  const clearSelection = () => {
    setSelectedOption(null);
    onChange?.("", null);
    closeDropDown();
  };

  React.useEffect(() => {
    window.document.addEventListener("click", onOutsideClick, true);

    return () => {
      window.document.removeEventListener("click", onOutsideClick, true);
    };
  }, [rootElementRef, searchFilter]);

  React.useEffect(() => {
    if (!triggerElRef) return;

    triggerElRef.addEventListener("keydown", handleUserKeyPress);

    return () => {
      triggerElRef.removeEventListener("keydown", handleUserKeyPress);
    };
  }, [handleUserKeyPress, triggerElRef]);

  React.useEffect(() => {
    onFilter?.(searchFilter).then((results) => {
      if (listElement) {
        listElement.scrollTop = 0;
      }

      setFilterResults(results);

      setSelectedElement(0);
    });
  }, [debouncedSearchFilter, updateCounter]);

  React.useEffect(() => {
    if (listElement && isOpen.state) {
      listElement.style.display = "block";
      updatePopper?.();
    }

    const onAnimationEnd = () => {
      if (listElement) {
        listElement.style.display = "none";
      }
    };

    if (!isOpen.state) {
      listElement?.addEventListener("animationend", onAnimationEnd);
    }

    return () => {
      listElement?.removeEventListener("animationend", onAnimationEnd);
    };
  }, [listElement, isOpen.state]);

  React.useEffect(() => {
    setSelectedOption(valueOption || null);
  }, [JSON.stringify(valueOption)]);

  const resultsDropDownOpenAnimation = `${resultsDropDownOpenAnimationKeyFrames} .2s ease-in-out`;
  const resultsDropDownCloseAnimation = `${resultsDropDownCloseAnimationKeyFrames} .2s ease-in-out`;

  return (
    <FormControlWrapper
      onClick={onRootElClick}
      ref={setRootElementRef}
      {...rest}
    >
      <Flex
        border={"1px solid"}
        borderColor={rest.error ? "semantic.error" : "border.input"}
        borderRadius={4}
        px={4}
        cursor={isDisabled ? "default" : "text"}
        flexWrap={"wrap"}
        w={"100%"}
        minH={"50px"}
        alignItems={"center"}
        pos={"relative"}
      >
        {(!isOpen.state || searchFilter === "") && selectedOption ? (
          <Flex
            px={4}
            minH={"50px"}
            pos={"absolute"}
            left={0}
            right={0}
            top={0}
            bottom={0}
            alignItems={"center"}
            opacity={isOpen.state ? 0.2 : 1}
          >
            {renderValueOption
              ? renderValueOption(selectedOption, false)
              : renderOption
              ? renderOption(selectedOption, false)
              : selectedOption.label}
          </Flex>
        ) : undefined}
        {placeholder &&
        (!isOpen.state || searchFilter === "") &&
        !selectedOption ? (
          <Flex
            px={4}
            minH={"50px"}
            pos={"absolute"}
            left={0}
            right={0}
            top={0}
            bottom={0}
            alignItems={"center"}
            color={"#A0AEC0"}
            opacity={isOpen.state ? 0.2 : 1}
          >
            {placeholder}
          </Flex>
        ) : undefined}
        <input
          autoComplete={"off"}
          ref={setTriggerElRef}
          style={{
            border: 1,
            ...(isOpen.state && { zIndex: "dropdown", position: "relative" }),
            padding: "12px 0",
            margin: 0,
            width: "100%",
            fontSize: 16,
            fontFamily: "roboto, sans-serif",
            maxWidth: "100%",
            outline: "none",
            display: isOpen.state ? "block" : "none",
            background: "transparent",
          }}
          onChange={onFilterInputChange}
          value={searchFilter}
        />
        {isDisabled && (
          <Box
            px={4}
            pos={"absolute"}
            left={0}
            right={0}
            top={0}
            bottom={0}
            alignItems={"center"}
            bg={"#fff"}
            my={"2px"}
            mx={"2px"}
            opacity={0.6}
          />
        )}
      </Flex>
      <InputRightElement pt={2} mr={2}>
        <HStack spacing={2}>
          <Button
            dataTestId="deselect-button"
            visibility={
              isCancellable && selectedOption && !isDisabled
                ? "visible"
                : "hidden"
            }
            onClick={(e) => {
              e.stopPropagation();
              clearSelection();
            }}
            empty
          >
            <CloseIcon />
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              if (isOpen.state) {
                closeDropDown();
              } else {
                openDropDown();
              }
            }}
            empty
          >
            <ChevronDownIcon
              transition={"transform .2s"}
              transform={isOpen.state ? "rotate(-180deg)" : undefined}
            />
          </Button>
        </HStack>
      </InputRightElement>
      {isOpenOnce.state && (
        <Box
          ref={setListItem}
          style={styles.popper}
          {...attributes.popper}
          background={"bg.white"}
          left={0}
          right={0}
          w={rootElementRef?.offsetWidth}
          boxShadow="0px 2px 4px rgba(40, 41, 61, 0.04), 0px 8px 16px rgba(96, 97, 112, 0.16)"
          borderWidth={1}
          borderStyle={"solid"}
          borderColor={"border.grey"}
          zIndex={"dropdown"}
          maxH={"224px"}
          overflow={"auto"}
          animation={
            isOpen.state
              ? resultsDropDownOpenAnimation
              : resultsDropDownCloseAnimation
          }
        >
          {filterResults.length === 0 && (
            <Box textAlign={"center"} padding={4}>
              No records found
            </Box>
          )}
          {filterResults.map((option, optionIndex) => {
            return (
              <DropDownOptionElement<T>
                option={option}
                index={optionIndex}
                key={`${option.value}-${optionIndex}`}
                selectedElement={!option.disabled && selectedElement}
                selectedOption={selectedOption}
                setSelectedElement={setSelectedElement}
                onSelect={onOptionSelect}
                disabled={option.disabled || false}
                wrapperOptions={wrapperOptions && wrapperOptions(option)}
                {...optionProps}
              >
                {renderOption ? renderOption(option, true) : option.label}
              </DropDownOptionElement>
            );
          })}
        </Box>
      )}
    </FormControlWrapper>
  );
};

export default VeriCustomSelect;
